import { FC } from "react";
import styled from "styled-components/macro";
import { motion } from "framer-motion";
import PortfolioCard, { CirclePositionType } from "./PortfolioCard";

const StyledContent = styled.div`
  font-family: poppins, sans-serif;
  text-align: left;
  width: 100%;
  .portfolio-header {
    display: flex;
    flex-direction: row;
    max-width: 900px;
    margin: 70px 0 100px 0;

    @media screen and (max-width: 1000px) {
      margin: 20px 0 50px 0;
    }
  }

  .portfolio-cards-grid {
    display: grid;
    grid-template-columns: 0.5fr 1fr;
    gap: 80px;
    margin: 150px 0;
    max-width: 900px;

    @media screen and (max-width: 1000px) {
      grid-template-columns: 0.5fr;
      margin-top: 50px;
    }
  }
`;

const Title = styled(motion.h2)`
  color: rgba(29, 91, 206, 0.2);
  font-family: "DM Serif Display", serif;
  font-size: 220px;
  letter-spacing: 1px;
  font-weight: 400;
  margin: auto;
  text-align: center;

  @media screen and (max-width: 1000px) {
    font-size: 80px;
  }
`;

const Description = styled.p`
  color: #1d5cce;
  margin: 0px;
  max-width: 520px;
  font-size: 24px;
  letter-spacing: 1px;
  font-family: "DM Sans", serif;
  padding: 10px 0px 0px 0px;
  line-height: 1.5;
  text-align: center;

  @media screen and (max-width: 1000px) {
    font-size: 20px;
    padding: 10px 20px 0px 20px;
  }
`;

// ====== Animation Variants ======
const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    // width: "100%",
    transition: { staggerChildren: 0.3, delayChildren: 0.2 },
  },
};

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } },
};

const PortfolioPage: FC = () => (
  <StyledContent>
    <motion.div
      style={{
        background: "white",
        justifyContent: "center",
        display: "flex",
        width: "100vw",
        minHeight: "60vh",
      }}
      variants={containerVariants}
      initial="hidden"
      animate="show"
    >
      <div className="">
        <motion.div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
          variants={containerVariants}
          className="portfolio-header"
        >
          <Title variants={fadeUp}>Portfolio</Title>
          <motion.div variants={fadeUp}>
            <div
              style={{
                fontSize: 12,
                textAlign: "center",
                fontFamily: "poppins, sans-serif",
                fontWeight: 600,
                letterSpacing: 1,
                color: "rgba(29, 91, 206, 0.2)",
              }}
            >
              LEARN MORE
            </div>
            <Description>
              Explore my work, skills and interests as a Software Engineer,
              including professional experience, projects and education.
            </Description>{" "}
          </motion.div>
          <motion.div
            style={{
              width: "100px",
              height: "0",
              borderTop: "1px solid #D3DEF7",
              margin: "24px 16px 60px 16px",
            }}
            variants={fadeUp}
          />

          <motion.div variants={fadeUp}>
            <PortfolioCard
              title="Radar"
              description="A personal Tech Radar showcasing the technologies I currently use."
              linkText="VIEW TECH RADAR"
              circle={true}
              link="/techstack"
              circlePosition="left"
              hoverColor="linear-gradient(180deg, #FFA600 0%, #FF7C74 50%, #E6236D 100%)"
            />
          </motion.div>
        </motion.div>
      </div>
    </motion.div>

    {/* PORTFOLIO GRID SECTION */}
    <motion.div
      style={{
        justifyContent: "center",
        display: "flex",
      }}
      variants={containerVariants}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.2 }}
    >
      <motion.div className="portfolio-cards-grid" variants={containerVariants}>
        {[
          {
            title: "Experience",
            description:
              "A summary of experiences across various industries including finance, technology and others.",
            linkText: "VIEW EXPERIENCE",
            link: "/experience",
            hoverColor: "#4b5e79ff",
          },
          {
            title: "Projects",
            description:
              "A showcase of selected projects demonstrating my skills and expertise.",
            linkText: "VIEW PROJECTS",
            link: "/projects",
            hoverColor: "#ba87d9ff",
          },
          {
            title: "Education",
            description:
              "My academic background, degrees, and relevant coursework.",
            linkText: "VIEW EDUCATION",
            link: "/education",
            hoverColor: "#f9aa21ff",
          },
          {
            title: "Certifications",
            description:
              "Certifications and credentials I have earned in technology and development.",
            linkText: "VIEW CERTIFICATES",
            link: "/certifications",
            hoverColor: "#ff3d7eff",
          },
        ].map((card, idx) => (
          <motion.div key={idx} variants={fadeUp}>
            <PortfolioCard
              title={card.title}
              description={card.description}
              linkText={card.linkText}
              link={card.link}
              circlePosition={
                ["top", "right", "left", "bottom"][
                  idx % 4
                ] as CirclePositionType
              }
              hoverColor={card.hoverColor}
            />
          </motion.div>
        ))}
      </motion.div>
    </motion.div>
  </StyledContent>
);

export default PortfolioPage;
